import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Phone,
  MessageCircle,
  Mail,
  Users,
  Bot,
  FileText,
  Clock,
  User,
  Timer,
  Calendar,
  ClipboardList,
  Plus
} from 'lucide-react';
import {
  ActivityDto,
  ActivityType,
  CallResult,
  EntityType,
  formatActivityDate,
  formatDuration,
  getCallResultColor,
  activityApi,
  handleActivityApiError
} from '../services/activityService';

interface ActivityTimelineProps {
  entityType: EntityType;
  entityId: number;
  onActivityAdded?: () => void;
  showAddButton?: boolean;
  hideHeader?: boolean;
  onCountChange?: (count: number) => void;
  refreshKey?: number;
}

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TimelineTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const ActivityCount = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: normal;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--primary-light);
    color: var(--primary-color);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  color: var(--text-secondary);
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityCard = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ActivityInfo = styled.div`
  flex: 1;
`;

const ActivityTypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

const ActivityIcon = styled.span<{ $activityType?: ActivityType }>`
  display: flex;
  align-items: center;
  color: ${props => {
    switch (props.$activityType) {
      case ActivityType.PhoneCall:
        return 'var(--primary-color)';
      case ActivityType.WhatsApp:
        return '#25D366';
      case ActivityType.Email:
        return '#EA4335';
      case ActivityType.Meeting:
        return '#FF6900';
      case ActivityType.SystemGenerated:
        return 'var(--text-tertiary)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const ActivityTime = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: var(--primary-color);
  }
`;

const ActivityUser = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: #6c5ce7;
  }
`;

const ActivityDuration = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: #00b894;
  }
`;

const ActivityContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
`;

const getCallResultBackground = (result: CallResult): string => {
  switch (result) {
    case CallResult.Interested_FollowUp:
    case CallResult.Interested_ReadyToSign:
      return 'rgba(22, 163, 74, 0.1)';
    case CallResult.Undecided_CallBack:
      return 'rgba(217, 119, 6, 0.1)';
    case CallResult.NotAvailable:
    case CallResult.NoAnswer:
      return 'rgba(128, 128, 128, 0.1)';
    case CallResult.NotInterested:
    case CallResult.WrongNumber:
      return 'rgba(220, 38, 38, 0.1)';
    default:
      return 'rgba(128, 128, 128, 0.1)';
  }
};

const getCallResultBorder = (result: CallResult): string => {
  switch (result) {
    case CallResult.Interested_FollowUp:
    case CallResult.Interested_ReadyToSign:
      return 'rgba(22, 163, 74, 0.3)';
    case CallResult.Undecided_CallBack:
      return 'rgba(217, 119, 6, 0.3)';
    case CallResult.NotAvailable:
    case CallResult.NoAnswer:
      return 'rgba(128, 128, 128, 0.3)';
    case CallResult.NotInterested:
    case CallResult.WrongNumber:
      return 'rgba(220, 38, 38, 0.3)';
    default:
      return 'rgba(128, 128, 128, 0.3)';
  }
};

const CallResultBadge = styled.span<{ result: CallResult }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: var(--border-radius-md);
  font-size: 13px;
  font-weight: 500;
  color: ${props => getCallResultColor(props.result)};
  background: ${props => getCallResultBackground(props.result)};
  border: 1px solid ${props => getCallResultBorder(props.result)};
  margin-top: 10px;
`;

const CallBackInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: var(--warning-light);
  border: 1px solid #d97706;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  color: var(--warning-dark);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid #dc2626;
`;

const getActivityIcon = (type: ActivityType): React.ReactNode => {
  const iconProps = { size: 16, strokeWidth: 2 };
  switch (type) {
    case ActivityType.PhoneCall:
      return <Phone {...iconProps} />;
    case ActivityType.WhatsApp:
      return <MessageCircle {...iconProps} />;
    case ActivityType.Email:
      return <Mail {...iconProps} />;
    case ActivityType.Meeting:
      return <Users {...iconProps} />;
    case ActivityType.SystemGenerated:
      return <Bot {...iconProps} />;
    default:
      return <FileText {...iconProps} />;
  }
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  entityType,
  entityId,
  onActivityAdded,
  showAddButton = false,
  hideHeader = false,
  onCountChange,
  refreshKey
}) => {
  const [activities, setActivities] = useState<ActivityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await activityApi.getByEntity(entityType, entityId);
      setActivities(data);
      onCountChange?.(data.length);
    } catch (err) {
      setError(handleActivityApiError(err));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, onCountChange]);

  useEffect(() => {
    loadActivities();
  }, [entityType, entityId, loadActivities, refreshKey]);

  if (loading) {
    return (
      <TimelineContainer>
        <LoadingContainer>טוען היסטוריית פעילויות...</LoadingContainer>
      </TimelineContainer>
    );
  }

  if (error) {
    return (
      <TimelineContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      {!hideHeader && (
        <TimelineHeader>
          <TimelineTitle>
            היסטוריית פעילויות
            <ActivityCount> ({activities.length})</ActivityCount>
          </TimelineTitle>
          {showAddButton && onActivityAdded && (
            <AddButton onClick={onActivityAdded}>
              <Plus size={16} /> הוסף פעילות
            </AddButton>
          )}
        </TimelineHeader>
      )}

      {activities.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <ClipboardList size={48} />
          </EmptyIcon>
          <EmptyText>אין פעילויות רשומות עדיין</EmptyText>
        </EmptyState>
      ) : (
        <ActivityList>
          {activities.map((activity) => (
            <ActivityCard key={activity.id}>
              <ActivityHeader>
                <ActivityInfo>
                  <ActivityTypeLabel>
                    <ActivityIcon $activityType={activity.activityType}>{getActivityIcon(activity.activityType)}</ActivityIcon>
                    {activity.activityTypeName}
                  </ActivityTypeLabel>
                  <ActivityMeta>
                    <ActivityTime>
                      <Clock size={12} />
                      {formatActivityDate(activity.createdAt)}
                    </ActivityTime>
                    <ActivityUser>
                      <User size={12} />
                      {activity.performedByUserName}
                    </ActivityUser>
                    {activity.durationMinutes && (
                      <ActivityDuration>
                        <Timer size={12} />
                        {formatDuration(activity.durationMinutes)}
                      </ActivityDuration>
                    )}
                  </ActivityMeta>
                </ActivityInfo>
              </ActivityHeader>

              {activity.description && (
                <ActivityContent>{activity.description}</ActivityContent>
              )}

              {activity.callResult && (
                <CallResultBadge result={activity.callResult}>
                  {activity.callResultName}
                </CallResultBadge>
              )}

              {activity.callBackTimeframe && (
                <CallBackInfo>
                  <Calendar size={14} />
                  להתקשר שוב: {activity.callBackTimeframe}
                </CallBackInfo>
              )}
            </ActivityCard>
          ))}
        </ActivityList>
      )}
    </TimelineContainer>
  );
};

export default ActivityTimeline;